import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { send } from "../api/auth";
import { expenses as api, type EventExpenseClaim, type EventExpenses } from "../api/expenses";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import type { Tone } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { money, whenIn } from "../system/format";
import { minorFrom } from "../system/payRules";

// ---------------------------------------------------------------------------
// Sweep §4 / 0190. AN EVENT'S EXPENSE CLAIMS, for whoever holds expense.decide.
//
// NOT PAY. Shown on its own screen and never added to a pay figure: a
// reimbursement is taxed differently from earnings. The totals arrive computed
// by the database (app.event_expense_totals); this screen formats them. A
// decided claim cannot be changed — the database refuses it — so a decided row
// has no controls.
// ---------------------------------------------------------------------------

export function Expenses() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const eventId = useParams()["id"] ?? "";
  const [data, setData] = useState<EventExpenses | null>(null);
  const [failed, setFailed] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<EventExpenses> & { error?: { message?: string } }>(api.forEvent(apiBase, eventId));
      if (res.status === 200 && Array.isArray(res.body.claims)) {
        setData(res.body as EventExpenses);
        setFailed(null);
      } else {
        // NULL, NOT AN EMPTY LIST: "nobody claimed anything" and "this could not be
        // read" are opposite facts.
        setData(null);
        setFailed({ status: res.status, refusal: refusal(res.body.error) });
      }
    } catch {
      setData(null);
      setFailed({ status: 0, refusal: t("We could not reach Verbunk. Check your connection and try again.") });
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("Expenses")}</h1>
      </div>
      <p className="muted">
        {t("Money the crew spent on their shifts and claimed back. Not pay: a reimbursement is taxed differently and is never part of anybody's gross.")}
      </p>
      {failed ? <LoadFailed failure={failed} fallback={t("The expense claims for this event could not be read.")} onRetry={() => void load()} /> : null}
      {data && data.totals.length > 0 ? (
        <section className="panel">
          <h2>{t("Approved on this event")}</h2>
          <ul className="plain-list">
            {data.totals.map((x) => (
              <li key={x.currency} className="row">
                <strong>{money(x.approved_minor, x.currency)}</strong>
                <span className="muted">
                  {t("approved in {n} claim(s), of which {recharged} is recharged to the client · {waiting} waiting for a decision", {
                    n: x.approved_count,
                    recharged: money(x.rechargeable_minor, x.currency),
                    waiting: x.waiting_count,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {data && data.may_reimburse ? <Reimburse data={data} onPaid={load} /> : null}
      {data && data.claims.length === 0 ? (
        <section className="panel">
          <p className="muted">{t("Nobody has claimed an expense against this event.")}</p>
        </section>
      ) : null}
      {data && data.claims.length > 0 ? (
        <section className="panel">
          <ul className="plain-list">
            {data.claims.map((c) => (
              <ClaimRow key={c.id} claim={c} data={data} orgZone={orgZone} onDecided={load} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function ClaimRow({
  claim: c,
  data,
  orgZone,
  onDecided,
}: {
  claim: EventExpenseClaim;
  data: EventExpenses;
  orgZone: string | null;
  onDecided: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function decide(body: Record<string, unknown>) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(api.decide(apiBase, c.id, body));
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("That decision could not be recorded."));
        return;
      }
      await onDecided();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const unitLabel = data.units.find((u) => u.id === c.distance_unit)?.label;
  const unit = unitLabel === undefined ? c.distance_unit : translateLabel(locale, unitLabel);
  // S145. Each trailing fact is a whole sentence in the catalogue, joined as before.
  const facts = [
    c.rechargeable === null ? null : c.rechargeable ? t("Recharged to the client.") : t("Not recharged to the client."),
    c.rejection_reason_label ? translateLabel(locale, c.rejection_reason_label) : null,
    c.decided_at
      ? c.decided_by_you
        ? t("decided {when} by you", { when: whenIn(c.decided_at, orgZone) })
        : t("decided {when} by a colleague", { when: whenIn(c.decided_at, orgZone) })
      : null,
    c.invoice_reference ? t("invoiced on {reference}", { reference: c.invoice_reference }) : null,
    c.reimbursement_reference ? t("reimbursed in {reference}", { reference: c.reimbursement_reference }) : null,
  ].filter((f): f is string => f !== null);

  return (
    <li className="stack">
      <div className="row">
        <StatusChip tone={c.state_tone as Tone} label={translateLabel(locale, c.state_label)} />
        <strong>{money(c.amount_minor, c.currency)}</strong>
        <span>{translateLabel(locale, c.kind_label)}</span>
        <span className="muted">
          {c.worker_name} · {translateLabel(locale, c.role_name)} · {t("shift {when}", { when: whenIn(c.shift_start, orgZone) })}
        </span>
      </div>
      {c.distance !== null ? (
        <p className="muted">
          {t("{distance} {unit} at {rate} each", { distance: c.distance, unit: unit ?? "", rate: money(c.rate_minor, c.currency) })}
        </p>
      ) : null}
      <p className="muted">
        {c.receipt_document_id ? (
          data.may_open_receipts ? (
            <Link to={`/console/documents/${c.receipt_document_id}`}>{t("Open the receipt")}</Link>
          ) : (
            t("A receipt is attached.")
          )
        ) : (
          t("No receipt attached.")
        )}
        {/* The recharge decision is recorded only with an approval, so its presence
            says so without this screen naming the state. */}
        {facts.map((f) => ` · ${f}`).join("")}
      </p>
      {c.awaits_decision && data.approve_state && data.reject_state ? (
        <div className="row">
          <button type="button" className="btn" disabled={busy} onClick={() => void decide({ state: data.approve_state, rechargeable: true })}>
            {t("Approve and recharge")}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy}
            onClick={() => void decide({ state: data.approve_state, rechargeable: false })}
          >
            {t("Approve, agency pays")}
          </button>
          <label className="visually-hidden" htmlFor={`reject-${c.id}`}>
            {t("Why it is not approved")}
          </label>
          <select id={`reject-${c.id}`} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">{t("Reject because…")}</option>
            {data.reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {translateLabel(locale, r.label)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy || reason === ""}
            onClick={() => void decide({ state: data.reject_state, reason })}
          >
            {t("Reject")}
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not recorded")} />
          <span>{notice}</span>
        </div>
      ) : null}
    </li>
  );
}

/**
 * S66 (0210). Pays the approved, not-yet-reimbursed claims on this event to the
 * crew as a payment batch of their own. Every approved claim is owed to the worker,
 * recharged or not. The amounts are the claims'; nothing is added up here.
 */
function Reimburse({ data, onPaid }: { data: EventExpenses; onPaid: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // An approval records the recharge decision; nothing else does (0224's note).
  const owed = data.claims.filter((c) => c.rechargeable !== null && c.reimbursement_reference === null);
  if (owed.length === 0) return null;

  async function pay(provider: string) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        api.reimburse(apiBase, provider, owed.map((c) => c.id)),
      );
      if (res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("The reimbursement could not be paid."));
        return;
      }
      await onPaid();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack">
      <h2>{t("Reimburse the crew")}</h2>
      <p className="muted">
        {t("{n} approved claim(s) not yet paid back. They go out as their own payment batch: a reimbursement, not wages, so they never appear in the payroll export.", { n: owed.length })}
      </p>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not paid")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        {data.provider ? (
          <button type="button" className="btn" disabled={busy} onClick={() => void pay(data.provider as string)}>
            {busy ? t("Working…") : t("Pay back the approved claims")}
          </button>
        ) : (
          <span className="muted">{t("No payment rail is enabled, so nothing can be paid from here.")}</span>
        )}
      </div>
    </section>
  );
}

/** S144. Also on the Organisation settings screen, which reads the same two fields from GET /v1/expenses/mileage-rate. */
export function MileageRate({
  data,
  onSaved,
}: {
  data: Pick<EventExpenses, "units" | "mileage">;
  onSaved: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const m = data.mileage;
  const currency = m?.org_currency ?? "";
  const [unit, setUnit] = useState(m?.unit ?? data.units[0]?.id ?? "");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function save() {
    const minor = minorFrom(rate, currency);
    if (minor === null || minor <= 0) {
      setNotice(t("Type the amount paid for each unit of distance, such as 15."));
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(api.setMileageRate(apiBase, { unit, rate_minor: minor }));
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("The mileage rate could not be saved."));
        return;
      }
      setRate("");
      await onSaved();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const current =
    m && m.unit && m.rate_minor
      ? t("{rate} per {unit}", {
          rate: money(m.rate_minor, m.currency),
          unit: translateLabel(locale, data.units.find((u) => u.id === m.unit)?.label ?? m.unit),
        })
      : t("not set — the crew cannot claim mileage until it is");

  return (
    <form
      className="panel stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h2>{t("Mileage rate")}</h2>
      <p className="muted">
        {t("One rate for the whole agency, usually the tax authority's figure. A claim keeps the rate it was made at, so changing it moves no claim already made. Currently {current}.", { current })}
      </p>
      <div className="row">
        <div className="field">
          <label htmlFor="mileage-rate">{t("Paid per unit ({currency})", { currency })}</label>
          <input id="mileage-rate" inputMode="decimal" required value={rate} onChange={(e) => setRate(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="mileage-unit">{t("Unit")}</label>
          <select id="mileage-unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            {data.units.map((u) => (
              <option key={u.id} value={u.id}>
                {translateLabel(locale, u.label)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not saved")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("Saving…") : t("Save the rate")}
        </button>
      </div>
    </form>
  );
}
